/**
 * ORB源石 17-level tier system.
 *
 * Three tiers:
 *   E (Entry)  — E1 一星, E2 二星, E3 三星
 *   A (Adv)    — A1 水星 … A5 木星
 *   S (Senior) — S1 地月系大使 … S9 宇宙大使
 *
 * Business rules derived from the authoritative UO.pdf reference document.
 * Each level encodes:
 *   - Promotion requirements (entry_fee, direct_e2, small_market_perf, donation_uo, a5_in_small_market)
 *   - Weekly EP cap (weekly_ep_cap, weekly_ep_cap_sub for 100$ sub-accounts)
 *   - Mentorship bonus circles (mentor_circles: depth → pct)
 *   - Promotion bonus (promotion_rp — one-time RP reward, S-tier only)
 *
 * Core rule: 晋级时只计算直推的主账户，子账户不算直推数量统计
 * (Promotion counts only direct referrals that are main accounts; sub-accounts don't count.)
 */

/* ------------------------------------------------------------------ */
/*  Type definitions                                                   */
/* ------------------------------------------------------------------ */

export type LevelTier = 'E' | 'A' | 'S';

/** Circle-based mentorship bonus percentages. */
export interface MentorCircle {
  /** Circle depth range [from, to] (1-indexed). */
  from: number;
  to: number;
  /** Percentage of the balance-award (平衡奖) base. e.g. 0.40 = 40%. */
  pct: string;
}

export interface LevelDef {
  /** Level code: E1, E2, E3, A1–A5, S1–S9. */
  code: string;
  /** Chinese display name. */
  name: string;
  /** Tier category. */
  tier: LevelTier;
  /** Sort order (0 = lowest). */
  sort: number;

  /* --- Promotion requirements --- */
  /** USDT entry fee (E-tier) or 0 for A/S tiers. */
  entry_fee: string;
  /** Number of direct E2-level referrals required (A-tier). */
  direct_e2: number;
  /** Small market performance threshold in USDT. '0' if none. */
  small_market_perf: string;
  /** Cumulative UO donation threshold. '0' if none. */
  donation_uo: string;
  /** Donation title awarded at this level. null if none. */
  donation_title: string | null;
  /** A5 (木星) count required in small market (S-tier). 0 if N/A. */
  a5_in_small_market: number;

  /* --- Weekly EP cap --- */
  /** Standard weekly EP earning cap. */
  weekly_ep_cap: string;
  /** Weekly EP cap for sub-accounts of 100$ main accounts. '0' if N/A. */
  weekly_ep_cap_sub: string;

  /* --- Mentorship bonus (辅导奖) --- */
  /**
   * Circle-based mentorship bonus distribution.
   * Empty array = no mentorship bonus (E-tier).
   * Base: 平衡奖 (balance award) of direct referral line.
   * Rule: 子账户不享受此奖励 (sub-accounts don't receive this).
   */
  mentor_circles: MentorCircle[];

  /* --- Promotion bonus (晋级奖) --- */
  /** One-time RP reward on promotion to this level. '0' if none. */
  promotion_rp: string;
}

/* ------------------------------------------------------------------ */
/*  17-level definitions                                               */
/* ------------------------------------------------------------------ */

export const USER_LEVELS: readonly LevelDef[] = [
  // ── E-tier: Entry ────────────────────────────────────────────────
  {
    code: 'E1', name: '一星', tier: 'E', sort: 0,
    entry_fee: '100', direct_e2: 0, small_market_perf: '0', donation_uo: '0',
    donation_title: null, a5_in_small_market: 0,
    weekly_ep_cap: '300', weekly_ep_cap_sub: '0',
    mentor_circles: [],
    promotion_rp: '0',
  },
  {
    code: 'E2', name: '二星', tier: 'E', sort: 1,
    entry_fee: '1000', direct_e2: 0, small_market_perf: '0', donation_uo: '0',
    donation_title: null, a5_in_small_market: 0,
    weekly_ep_cap: '3000', weekly_ep_cap_sub: '0',
    mentor_circles: [],
    promotion_rp: '0',
  },
  {
    code: 'E3', name: '三星', tier: 'E', sort: 2,
    entry_fee: '3000', direct_e2: 0, small_market_perf: '0', donation_uo: '0',
    donation_title: null, a5_in_small_market: 0,
    weekly_ep_cap: '4000', weekly_ep_cap_sub: '0',
    mentor_circles: [],
    promotion_rp: '0',
  },

  // ── A-tier: Advanced ─────────────────────────────────────────────
  {
    code: 'A1', name: '水星', tier: 'A', sort: 3,
    entry_fee: '0', direct_e2: 5, small_market_perf: '5000', donation_uo: '50',
    donation_title: 'E3爱心志愿者', a5_in_small_market: 0,
    weekly_ep_cap: '4000', weekly_ep_cap_sub: '500',
    mentor_circles: [{ from: 1, to: 1, pct: '0.40' }],
    promotion_rp: '0',
  },
  {
    code: 'A2', name: '火星', tier: 'A', sort: 4,
    entry_fee: '0', direct_e2: 10, small_market_perf: '30000', donation_uo: '250',
    donation_title: 'E4爱心达人', a5_in_small_market: 0,
    weekly_ep_cap: '5000', weekly_ep_cap_sub: '1000',
    mentor_circles: [
      { from: 1, to: 1, pct: '0.40' },
      { from: 2, to: 2, pct: '0.10' },
    ],
    promotion_rp: '0',
  },
  {
    code: 'A3', name: '金星', tier: 'A', sort: 5,
    entry_fee: '0', direct_e2: 15, small_market_perf: '210000', donation_uo: '1000',
    donation_title: 'E5公益明星', a5_in_small_market: 0,
    weekly_ep_cap: '10000', weekly_ep_cap_sub: '1500',
    mentor_circles: [
      { from: 1, to: 1, pct: '0.40' },
      { from: 2, to: 3, pct: '0.10' },
    ],
    promotion_rp: '0',
  },
  {
    code: 'A4', name: '土星', tier: 'A', sort: 6,
    entry_fee: '0', direct_e2: 20, small_market_perf: '1000000', donation_uo: '2500',
    donation_title: 'E6公益大使', a5_in_small_market: 0,
    weekly_ep_cap: '20000', weekly_ep_cap_sub: '2000',
    mentor_circles: [
      { from: 1, to: 1, pct: '0.40' },
      { from: 2, to: 4, pct: '0.10' },
    ],
    promotion_rp: '0',
  },
  {
    code: 'A5', name: '木星', tier: 'A', sort: 7,
    entry_fee: '0', direct_e2: 25, small_market_perf: '5000000', donation_uo: '10000',
    donation_title: 'A1慈善家', a5_in_small_market: 0,
    weekly_ep_cap: '30000', weekly_ep_cap_sub: '2500',
    mentor_circles: [
      { from: 1, to: 1, pct: '0.40' },
      { from: 2, to: 5, pct: '0.10' },
    ],
    promotion_rp: '0',
  },

  // ── S-tier: Senior (大使) ────────────────────────────────────────
  {
    code: 'S1', name: '地月系大使', tier: 'S', sort: 8,
    entry_fee: '0', direct_e2: 0, small_market_perf: '15000000', donation_uo: '25000',
    donation_title: 'A2银牌慈善家', a5_in_small_market: 3,
    weekly_ep_cap: '30000', weekly_ep_cap_sub: '3000',
    mentor_circles: [
      { from: 1, to: 1, pct: '0.40' },
      { from: 2, to: 5, pct: '0.10' },
      { from: 6, to: 6, pct: '0.05' },
    ],
    promotion_rp: '10000',
  },
  {
    code: 'S2', name: '太阳系大使', tier: 'S', sort: 9,
    entry_fee: '0', direct_e2: 0, small_market_perf: '30000000', donation_uo: '25000',
    donation_title: 'A2银牌慈善家', a5_in_small_market: 6,
    weekly_ep_cap: '30000', weekly_ep_cap_sub: '3500',
    mentor_circles: [
      { from: 1, to: 1, pct: '0.40' },
      { from: 2, to: 5, pct: '0.10' },
      { from: 6, to: 7, pct: '0.05' },
    ],
    promotion_rp: '20000',
  },
  {
    code: 'S3', name: '恒星系大使', tier: 'S', sort: 10,
    entry_fee: '0', direct_e2: 0, small_market_perf: '45000000', donation_uo: '25000',
    donation_title: 'A2银牌慈善家', a5_in_small_market: 9,
    weekly_ep_cap: '30000', weekly_ep_cap_sub: '4000',
    mentor_circles: [
      { from: 1, to: 1, pct: '0.40' },
      { from: 2, to: 5, pct: '0.10' },
      { from: 6, to: 7, pct: '0.05' },
    ],
    promotion_rp: '30000',
  },
  {
    code: 'S4', name: '银河系大使', tier: 'S', sort: 11,
    entry_fee: '0', direct_e2: 0, small_market_perf: '60000000', donation_uo: '25000',
    donation_title: 'A2银牌慈善家', a5_in_small_market: 12,
    weekly_ep_cap: '30000', weekly_ep_cap_sub: '4500',
    mentor_circles: [
      { from: 1, to: 1, pct: '0.40' },
      { from: 2, to: 5, pct: '0.10' },
      { from: 6, to: 7, pct: '0.05' },
    ],
    promotion_rp: '40000',
  },
  {
    code: 'S5', name: '麦哲伦星系大使', tier: 'S', sort: 12,
    entry_fee: '0', direct_e2: 0, small_market_perf: '75000000', donation_uo: '25000',
    donation_title: 'A2银牌慈善家', a5_in_small_market: 15,
    weekly_ep_cap: '30000', weekly_ep_cap_sub: '5000',
    mentor_circles: [
      { from: 1, to: 1, pct: '0.40' },
      { from: 2, to: 5, pct: '0.10' },
      { from: 6, to: 7, pct: '0.05' },
    ],
    promotion_rp: '50000',
  },
  {
    code: 'S6', name: '仙女座星系大使', tier: 'S', sort: 13,
    entry_fee: '0', direct_e2: 0, small_market_perf: '100000000', donation_uo: '25000',
    donation_title: 'A2银牌慈善家', a5_in_small_market: 20,
    weekly_ep_cap: '30000', weekly_ep_cap_sub: '5000',
    mentor_circles: [
      { from: 1, to: 1, pct: '0.40' },
      { from: 2, to: 5, pct: '0.10' },
      { from: 6, to: 7, pct: '0.05' },
    ],
    promotion_rp: '60000',
  },
  {
    code: 'S7', name: '本星系大使', tier: 'S', sort: 14,
    entry_fee: '0', direct_e2: 0, small_market_perf: '150000000', donation_uo: '25000',
    donation_title: 'A3金牌慈善家', a5_in_small_market: 30,
    weekly_ep_cap: '30000', weekly_ep_cap_sub: '5000',
    mentor_circles: [
      { from: 1, to: 1, pct: '0.40' },
      { from: 2, to: 5, pct: '0.10' },
      { from: 6, to: 7, pct: '0.05' },
    ],
    promotion_rp: '70000',
  },
  {
    code: 'S8', name: '总星系大使', tier: 'S', sort: 15,
    entry_fee: '0', direct_e2: 0, small_market_perf: '0', donation_uo: '25000',
    donation_title: 'A2银牌慈善家', a5_in_small_market: 40,
    weekly_ep_cap: '30000', weekly_ep_cap_sub: '5000',
    mentor_circles: [
      { from: 1, to: 1, pct: '0.40' },
      { from: 2, to: 5, pct: '0.10' },
      { from: 6, to: 7, pct: '0.05' },
    ],
    promotion_rp: '80000',
  },
  {
    code: 'S9', name: '宇宙大使', tier: 'S', sort: 16,
    entry_fee: '0', direct_e2: 0, small_market_perf: '0', donation_uo: '25000',
    donation_title: 'A2银牌慈善家', a5_in_small_market: 50,
    weekly_ep_cap: '30000', weekly_ep_cap_sub: '5000',
    mentor_circles: [
      { from: 1, to: 1, pct: '0.40' },
      { from: 2, to: 5, pct: '0.10' },
      { from: 6, to: 7, pct: '0.05' },
    ],
    promotion_rp: '100000',
  },
] as const;

/* ------------------------------------------------------------------ */
/*  Derived types and lookups                                          */
/* ------------------------------------------------------------------ */

export type UserLevelCode = (typeof USER_LEVELS)[number]['code'];

/** O(1) lookup by code. */
export const LEVEL_BY_CODE: Record<string, LevelDef> = Object.fromEntries(
  USER_LEVELS.map((lvl) => [lvl.code, lvl]),
) as Record<string, LevelDef>;

/** Sorted list of level codes in ascending order (E1 … S9). */
export const LEVEL_CODES_ASC: string[] = USER_LEVELS.map((l) => l.code);

/** Given a level code, return its 0-based sort index. Returns -1 if unknown. */
export function levelSortOf(code: string): number {
  const lvl = LEVEL_BY_CODE[code];
  return lvl ? lvl.sort : -1;
}

/** Returns true if `a` is a higher or equal level than `b`. */
export function isLevelGte(a: string, b: string): boolean {
  return levelSortOf(a) >= levelSortOf(b);
}

/**
 * Max mentorship circle depth for a given level.
 * E1-E3: 0 (no mentorship), A1: 1, A2: 2, ..., S2+: 7.
 */
export function maxMentorCircle(code: string): number {
  const lvl = LEVEL_BY_CODE[code];
  if (!lvl || lvl.mentor_circles.length === 0) return 0;
  return Math.max(...lvl.mentor_circles.map((c) => c.to));
}

/**
 * Get mentorship bonus percentage for a given level and circle depth.
 * Returns '0' if the level doesn't earn on that circle.
 */
export function mentorPctAtCircle(code: string, circle: number): string {
  const lvl = LEVEL_BY_CODE[code];
  if (!lvl) return '0';
  for (const mc of lvl.mentor_circles) {
    if (circle >= mc.from && circle <= mc.to) return mc.pct;
  }
  return '0';
}

/* ------------------------------------------------------------------ */
/*  Legacy compatibility                                               */
/* ------------------------------------------------------------------ */

/**
 * @deprecated Use LEVEL_BY_CODE directly. This is kept for backward compat
 * with code that referenced the old bonus_pct / min_weight fields.
 */
export const LEGACY_LEVEL_MAP: Record<string, { min_weight: string; bonus_pct: string }> =
  Object.fromEntries(
    USER_LEVELS.map((lvl) => [
      lvl.code,
      { min_weight: lvl.entry_fee, bonus_pct: '0' },
    ]),
  );
